import config from 'src/config';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';

import {
    riskInsightsOrganisationSitesQuery,
    type RiskInsightsOrganisationSitesResult,
} from 'src/app/portfolio/state/api/riskInsightsSitesQuery';
import { siteFilterOptionsQuery, type SiteFilterOptionsResult } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';
import { portfolioRiskStatsQuery, type PortfolioRiskStatsResult } from 'src/app/portfolio/state/api/portfolioRiskStatsQuery';
import {
    portfolioMaterialityBreakdownQuery,
    type PortfolioMaterialityBreakdownResult,
} from 'src/app/portfolio/state/api/portfolioMaterialityBreakdownQuery';
import {
    physicalRiskDistributionQuery,
    type PhysicalRiskDistributionResult,
} from 'src/app/portfolio/state/api/physicalRiskDistributionQuery';
import { portfolioStateOfNatureQuery, type PortfolioStateOfNatureResult } from 'src/app/portfolio/state/api/portfolioStateOfNatureQuery';
import {
    riskInsightsAvailableDatesQuery,
    type RiskInsightsAvailableDatesResult,
} from 'src/app/portfolio/state/api/riskInsightsAvailableDatesQuery';
import { executeEngineQuery } from './engineQueryUtils';
import { buildSitesCacheTagId } from './sitesCacheTagId';

export type SiteFilterOptionsQueryArgs = {
    businessCentralId: string;
};

export type RiskInsightsAvailableDatesQueryArgs = {
    businessCentralId: string;
};

export type RiskInsightsSitesSortField =
    | 'NAME'
    | 'CREATED_AT'
    | 'VALID_FROM'
    | 'PHYSICAL_NATURE_RISK'
    | 'OPERATIONAL_DEPENDENCIES'
    | 'IMPACTS_ON_NATURE'
    | 'STATE_OF_NATURE'
    | 'ASSESSMENT_DATE';

export type RiskInsightsSitesSortDirection = 'ASC' | 'DESC';

export type GqlRiskBandLevel = 'VERY_HIGH' | 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW';
export type GqlSonBandLevel = 'VERY_GOOD' | 'GOOD' | 'MODERATE' | 'POOR' | 'VERY_POOR';

export type RiskInsightsSitesQueryArgs = {
    businessCentralId: string;
    page: number;
    pageSize: number;
    nameContains?: string;
    sortBy?: RiskInsightsSitesSortField;
    sortDir?: RiskInsightsSitesSortDirection;
    isic?: string[];
    countries?: string[];
    physicalRiskLevels?: GqlRiskBandLevel[];
    dependencyLevels?: GqlRiskBandLevel[];
    impactLevels?: GqlRiskBandLevel[];
    sonLevels?: GqlSonBandLevel[];
    at?: string;
};

export type PortfolioRiskStatsQueryArgs = {
    businessCentralId: string;
    isic?: string[];
    countries?: string[];
    physicalRiskLevels?: GqlRiskBandLevel[];
    dependencyLevels?: GqlRiskBandLevel[];
    impactLevels?: GqlRiskBandLevel[];
    sonLevels?: GqlSonBandLevel[];
    at?: string;
};

const engineGraphqlEndpoint = `${config.engineApiUrl.replace(/\/$/, '')}/query`;

const riskInsightsEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        getSiteFilterOptions: builder.query<SiteFilterOptionsResult, SiteFilterOptionsQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteFilterOptionsResult>(engineGraphqlEndpoint, siteFilterOptionsQuery, {
                    businessCentralId: arg.businessCentralId,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'siteFilterOptions' as const, id: arg.businessCentralId }],
        }),
        getRiskInsightsSites: builder.query<RiskInsightsOrganisationSitesResult, RiskInsightsSitesQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<RiskInsightsOrganisationSitesResult>(engineGraphqlEndpoint, riskInsightsOrganisationSitesQuery, {
                    businessCentralId: arg.businessCentralId,
                    page: arg.page,
                    pageSize: arg.pageSize,
                    nameContains: arg.nameContains ?? null,
                    sortBy: arg.sortBy ?? null,
                    sortDir: arg.sortDir ?? null,
                    isic: arg.isic ?? [],
                    countries: arg.countries ?? [],
                    physicalRiskLevels: arg.physicalRiskLevels ?? [],
                    dependencyLevels: arg.dependencyLevels ?? [],
                    impactLevels: arg.impactLevels ?? [],
                    sonLevels: arg.sonLevels ?? [],
                    at: arg.at ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'sites' as const, id: buildSitesCacheTagId(arg) }],
        }),
        getPortfolioRiskStats: builder.query<PortfolioRiskStatsResult, PortfolioRiskStatsQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<PortfolioRiskStatsResult>(engineGraphqlEndpoint, portfolioRiskStatsQuery, {
                    businessCentralId: arg.businessCentralId,
                    isic: arg.isic ?? [],
                    countries: arg.countries ?? [],
                    physicalRiskLevels: arg.physicalRiskLevels ?? [],
                    dependencyLevels: arg.dependencyLevels ?? [],
                    impactLevels: arg.impactLevels ?? [],
                    sonLevels: arg.sonLevels ?? [],
                    at: arg.at ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'sites' as const, id: arg.businessCentralId }],
        }),
        getPortfolioMaterialityBreakdown: builder.query<PortfolioMaterialityBreakdownResult, PortfolioRiskStatsQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<PortfolioMaterialityBreakdownResult>(engineGraphqlEndpoint, portfolioMaterialityBreakdownQuery, {
                    businessCentralId: arg.businessCentralId,
                    isic: arg.isic ?? [],
                    countries: arg.countries ?? [],
                    physicalRiskLevels: arg.physicalRiskLevels ?? [],
                    dependencyLevels: arg.dependencyLevels ?? [],
                    impactLevels: arg.impactLevels ?? [],
                    sonLevels: arg.sonLevels ?? [],
                    at: arg.at ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'sites' as const, id: arg.businessCentralId }],
        }),
        getPortfolioStateOfNature: builder.query<PortfolioStateOfNatureResult, PortfolioRiskStatsQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<PortfolioStateOfNatureResult>(engineGraphqlEndpoint, portfolioStateOfNatureQuery, {
                    businessCentralId: arg.businessCentralId,
                    isic: arg.isic ?? [],
                    countries: arg.countries ?? [],
                    physicalRiskLevels: arg.physicalRiskLevels ?? [],
                    dependencyLevels: arg.dependencyLevels ?? [],
                    impactLevels: arg.impactLevels ?? [],
                    sonLevels: arg.sonLevels ?? [],
                    at: arg.at ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'sites' as const, id: arg.businessCentralId }],
        }),
        getPhysicalRiskDistribution: builder.query<PhysicalRiskDistributionResult, PortfolioRiskStatsQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<PhysicalRiskDistributionResult>(engineGraphqlEndpoint, physicalRiskDistributionQuery, {
                    businessCentralId: arg.businessCentralId,
                    isic: arg.isic ?? [],
                    countries: arg.countries ?? [],
                    physicalRiskLevels: arg.physicalRiskLevels ?? [],
                    dependencyLevels: arg.dependencyLevels ?? [],
                    impactLevels: arg.impactLevels ?? [],
                    sonLevels: arg.sonLevels ?? [],
                    at: arg.at ?? null,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'sites' as const, id: arg.businessCentralId }],
        }),
        getRiskInsightsAvailableDates: builder.query<RiskInsightsAvailableDatesResult, RiskInsightsAvailableDatesQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<RiskInsightsAvailableDatesResult>(engineGraphqlEndpoint, riskInsightsAvailableDatesQuery, {
                    businessCentralId: arg.businessCentralId,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'sites' as const, id: arg.businessCentralId }],
        }),
    }),
});

export const {
    useGetSiteFilterOptionsQuery,
    useGetRiskInsightsSitesQuery,
    useLazyGetRiskInsightsSitesQuery,
    useGetPortfolioRiskStatsQuery,
    useGetPortfolioMaterialityBreakdownQuery,
    useGetPortfolioStateOfNatureQuery,
    useGetPhysicalRiskDistributionQuery,
    useGetRiskInsightsAvailableDatesQuery,
} = riskInsightsEngineSlice;

export default riskInsightsEngineSlice;
